import { useState, useCallback, forwardRef } from 'react'
import Papa from 'papaparse'
import { TableVirtuoso } from 'react-virtuoso'
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material'
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material'
import { contactsApi, Contact, ImportContactsResult } from '../../services/api'
import {
  CONNECTION_TYPE_OPTIONS,
  DEFAULT_CONNECTION_TYPE,
  ConnectionType,
} from '../../constants/networking'

interface ImportLinkedInDialogProps {
  open: boolean
  onClose: () => void
  clientId: string
  onImportComplete: () => void
}

interface ParsedContact {
  firstName: string
  lastName: string
  email: string
  company: string
  position: string
  linkedinUrl: string
  connectedOn: string
  connectionType: ConnectionType
  selected: boolean
}

const steps = ['Upload CSV', 'Preview & Edit', 'Import']

const LINKEDIN_REQUIRED_HEADERS = ['First Name', 'Last Name']
const MAX_CONTACTS_PER_REQUEST = 5000

export default function ImportLinkedInDialog({
  open,
  onClose,
  clientId,
  onImportComplete,
}: ImportLinkedInDialogProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([])
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [search, setSearch] = useState('')
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<ImportContactsResult | null>(null)
  const [bulkConnectionType, setBulkConnectionType] = useState<ConnectionType>(DEFAULT_CONNECTION_TYPE)

  const reset = () => {
    setActiveStep(0)
    setParsedContacts([])
    setError('')
    setSearch('')
    setImporting(false)
    setProgress(0)
    setResult(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const parseFile = (file: File) => {
    setError('')

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a .csv file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('The file did not upload. Check the file size is under 10MB and try again.')
      return
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields || []

        // Validate LinkedIn format
        const hasRequired = LINKEDIN_REQUIRED_HEADERS.every((h) =>
          headers.some((header) => header.trim() === h)
        )

        if (!hasRequired) {
          setError(
            "This doesn't look like a LinkedIn export. Make sure you're uploading your Connections.csv file. Expected columns: First Name, Last Name"
          )
          return
        }

        const contacts: ParsedContact[] = (results.data as Record<string, string>[])
          .map((row) => ({
            firstName: (row['First Name'] || '').trim(),
            lastName: (row['Last Name'] || '').trim(),
            email: (row['Email Address'] || '').trim(),
            company: (row['Company'] || '').trim(),
            position: (row['Position'] || '').trim(),
            linkedinUrl: (row['URL'] || '').trim(),
            connectedOn: (row['Connected On'] || '').trim(),
            connectionType: DEFAULT_CONNECTION_TYPE,
            selected: true,
          }))
          .filter((c: ParsedContact) => c.firstName && c.lastName)

        if (contacts.length === 0) {
          setError('No valid contacts found in the CSV file')
          return
        }

        setParsedContacts(contacts)
        setActiveStep(1)
      },
      error: () => {
        setError('Failed to parse CSV file. Please check the file format.')
      },
    })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) parseFile(file)
    e.target.value = ''
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) parseFile(file)
  }, [])

  const toggleContact = (index: number) => {
    setParsedContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, selected: !c.selected } : c))
    )
  }

  const updateContactField = <K extends keyof ParsedContact>(
    index: number,
    field: K,
    value: ParsedContact[K]
  ) => {
    setParsedContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    )
  }

  const applyBulkConnectionType = () => {
    setParsedContacts((prev) =>
      prev.map((c) => (c.selected ? { ...c, connectionType: bulkConnectionType } : c))
    )
  }

  const toggleAll = () => {
    const filtered = getFilteredContacts()
    const allSelected = filtered.every((c) => c.selected)
    const filteredSet = new Set(filtered.map((_, i) => getFilteredIndices()[i]))
    setParsedContacts((prev) =>
      prev.map((c, i) => (filteredSet.has(i) ? { ...c, selected: !allSelected } : c))
    )
  }

  const bulkDeselect = (filter: (c: ParsedContact) => boolean) => {
    setParsedContacts((prev) => prev.map((c) => (filter(c) ? { ...c, selected: false } : c)))
  }

  const getFilteredContacts = () => {
    if (!search) return parsedContacts
    const q = search.toLowerCase()
    return parsedContacts.filter(
      (c) =>
        c.firstName.toLowerCase().includes(q) ||
        c.lastName.toLowerCase().includes(q) ||
        c.company.toLowerCase().includes(q) ||
        c.position.toLowerCase().includes(q)
    )
  }

  const getFilteredIndices = () => {
    if (!search) return parsedContacts.map((_, i) => i)
    const q = search.toLowerCase()
    return parsedContacts
      .map((c, i) => ({ c, i }))
      .filter(
        ({ c }) =>
          c.firstName.toLowerCase().includes(q) ||
          c.lastName.toLowerCase().includes(q) ||
          c.company.toLowerCase().includes(q) ||
          c.position.toLowerCase().includes(q)
      )
      .map(({ i }) => i)
  }

  const selectedCount = parsedContacts.filter((c) => c.selected).length

  const handleImport = async () => {
    setActiveStep(2)
    setImporting(true)
    setProgress(0)

    const selected = parsedContacts
      .filter((c) => c.selected)
      .map(({ selected: _, ...c }) => c)

    try {
      let totalImported = 0
      let totalSkipped = 0
      let batchId = ''

      // Chunk if needed
      for (let i = 0; i < selected.length; i += MAX_CONTACTS_PER_REQUEST) {
        const chunk = selected.slice(i, i + MAX_CONTACTS_PER_REQUEST)
        const res = await contactsApi.import(chunk as Partial<Contact>[], clientId)
        totalImported += res.imported || 0
        totalSkipped += res.skippedDuplicates || 0
        batchId = res.batchId || batchId
        setProgress(Math.round(((i + chunk.length) / selected.length) * 100))
      }

      setResult({ imported: totalImported, skippedDuplicates: totalSkipped, batchId })
    } catch (err) {
      setError('Import failed. Please try again.')
      setActiveStep(1)
    }
    setImporting(false)
  }

  const filtered = getFilteredContacts()

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            Import LinkedIn Contacts
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Step 1: Upload */}
          {activeStep === 0 && (
            <Box>
              <Paper
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  backgroundColor: dragOver ? 'action.hover' : 'transparent',
                  cursor: 'pointer',
                  mb: 3,
                }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag and drop your LinkedIn CSV here
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  or click the button below to select a file
                </Typography>
                <Button variant="contained" component="label">
                  Choose File
                  <input type="file" hidden accept=".csv" onChange={handleFileSelect} />
                </Button>
              </Paper>
            </Box>
          )}

          {/* Step 2: Preview */}
          {activeStep === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  We found {parsedContacts.length} contacts
                </Typography>
                <Chip label={`${selectedCount} of ${parsedContacts.length} selected`} color="primary" />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <Button size="small" variant="outlined" onClick={() => bulkDeselect((c) => !c.company)}>
                  Deselect without company
                </Button>
                <Button size="small" variant="outlined" onClick={() => bulkDeselect((c) => !c.email)}>
                  Deselect without email
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <Typography variant="body2" color="text.secondary">
                    Set selected to:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                      value={bulkConnectionType}
                      onChange={(e) => setBulkConnectionType(e.target.value as ConnectionType)}
                    >
                      {CONNECTION_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={applyBulkConnectionType}
                    disabled={selectedCount === 0}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>

              <Paper sx={{ mb: 3 }}>
                <TableVirtuoso
                  style={{ height: 480 }}
                  data={filtered}
                  fixedHeaderContent={() => (
                    <TableRow sx={{ backgroundColor: 'background.paper' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={filtered.length > 0 && filtered.every((c) => c.selected)}
                          indeterminate={filtered.some((c) => c.selected) && !filtered.every((c) => c.selected)}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>First Name</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Last Name</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>Company</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Position</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Email</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Connection Type</TableCell>
                    </TableRow>
                  )}
                  components={{
                    Table: (props) => <Table {...props} size="small" />,
                    TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
                    TableRow: (props) => <TableRow {...props} hover />,
                    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
                  }}
                  itemContent={(displayIndex, contact) => {
                    const realIndex = getFilteredIndices()[displayIndex]
                    return (
                      <>
                        <TableCell padding="checkbox">
                          <Checkbox checked={contact.selected} onChange={() => toggleContact(realIndex)} />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={contact.firstName}
                            onChange={(e) => updateContactField(realIndex, 'firstName', e.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={contact.lastName}
                            onChange={(e) => updateContactField(realIndex, 'lastName', e.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={contact.company}
                            onChange={(e) => updateContactField(realIndex, 'company', e.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={contact.position}
                            onChange={(e) => updateContactField(realIndex, 'position', e.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={contact.email}
                            onChange={(e) => updateContactField(realIndex, 'email', e.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth variant="standard">
                            <Select
                              value={contact.connectionType}
                              onChange={(e) =>
                                updateContactField(realIndex, 'connectionType', e.target.value as ConnectionType)
                              }
                            >
                              {CONNECTION_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </>
                    )
                  }}
                />
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={selectedCount === 0}
                >
                  Import {selectedCount} contacts
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Import Progress / Complete */}
          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {importing ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Importing {selectedCount} contacts...
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mb: 2 }}>
                    <LinearProgress variant="determinate" value={progress} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {progress}% complete
                  </Typography>
                </>
              ) : result ? (
                <>
                  <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Import Complete
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    {result.imported} contacts imported
                  </Typography>
                  {result.skippedDuplicates > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {result.skippedDuplicates} duplicates skipped
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => {
                      onImportComplete()
                      handleClose()
                    }}
                  >
                    View Side Door
                  </Button>
                </>
              ) : null}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
